import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { isEmpty } from 'ramda';

import { SpacerVertical, SpacerHorizontal } from '@klarna/bubble-ui';
import {
  defaultContainerPropTypes,
  FieldsetForObject,
  formUtils,
  HorizontalWrapper,
  payloadUtils,
} from 'moet-core';
import { generateCyElemId } from 'moet-test-utils/cypress/utils';

import { CAPS } from '@/constants';
import { BackButton, CallToActionButton, ContainerTemplate } from '@/components';
import { withCapabilityBeforeNext } from '@/flow/common';
import {
  getCountries,
  getRegistrationCourts,
  getGoodsCategories,
  getForcedRegistrationIdCountries,
} from '@/selectors/options';
import { countrySpecificTranslationParser } from '@/selectors/utils';

import { withFeatureToggledValidation } from '../withFeatureToggledValidation';
import { CY_PRE_ASSESSMENT_SME_COMPANY_DETAILS_CONTAINER_IDS, tPrefix } from './constants';
import { getDefaultState } from './selectors';
import { prepareFields, getValues } from './fields';

export class PreAssessmentSmeCompanyDetails extends React.Component {
  constructor(props) {
    super(props);

    const fields = prepareFields(props, tPrefix);
    this.state = { fields, initialState: { fields }, opCountryPrefilled: false };
    this.onChangeFields = this.onChangeFields.bind(this);
    this.getBody = this.getBody.bind(this);
    this.onNext = this.onNext.bind(this);
  }

  onChangeFields(fields, key) {
    let opCountryPrefilled = this.state.opCountryPrefilled && key !== 'operatingCountry';
    const shouldPrefill =
      key === 'registrationCountry' &&
      (isEmpty(fields.operatingCountry.value) || this.state.opCountryPrefilled);

    if (shouldPrefill) {
      fields.operatingCountry.value = fields.registrationCountry.value;
      opCountryPrefilled = true;
    }
    this.setState({ fields: prepareFields(this.props, tPrefix, fields), opCountryPrefilled });
  }

  getBody({ fields }) {
    return getValues(prepareFields(this.props, tPrefix, fields));
  }

  onNext() {
    const { mid, onNext } = this.props;
    const { fields } = this.state;

    this.setState({
      ...this.state,
      fields: formUtils.initializeFields(fields),
    });

    formUtils.callToActionOnNextWrapper(fields, () => {
      onNext(payloadUtils.getMerchantCapabilityPayload(mid, this.state, this.getBody));
    });
  }

  render() {
    const { error, hasBack, isDisabled, isLoading, onBack, t } = this.props;
    const { fields } = this.state;

    return (
      <ContainerTemplate
        title={t(`${tPrefix}.title`)}
        subtitle={t(`${tPrefix}.subtitle`)}
        error={error}
      >
        <SpacerVertical small />
        <FieldsetForObject
          name="pre-assessment-company-details"
          isBorderless
          isDisabled={isDisabled}
          fields={fields}
          onBlur={key => formUtils.onBlurFields(key, this.state, this.onChangeFields)}
          onChange={(key, val) =>
            formUtils.onChangeFields(key, val, this.state, this.onChangeFields)
          }
        />
        <SpacerVertical large />
        <HorizontalWrapper display="inline-flex" alignItems="center" justifyContent="flex-end">
          {hasBack && (
            <>
              <BackButton onClick={onBack} disabled={isDisabled}>
                {t(`${tPrefix}.buttons.back`)}
              </BackButton>
              <SpacerHorizontal medium />
            </>
          )}
          <CallToActionButton
            loading={isLoading}
            onClick={this.onNext}
            disabled={isDisabled}
            {...generateCyElemId(
              CY_PRE_ASSESSMENT_SME_COMPANY_DETAILS_CONTAINER_IDS.BTN_SUBMIT,
              window,
            )}
          >
            {t(`${tPrefix}.buttons.next`)}
          </CallToActionButton>
        </HorizontalWrapper>
      </ContainerTemplate>
    );
  }
}

PreAssessmentSmeCompanyDetails.propTypes = {
  ...defaultContainerPropTypes,
  forcedRegistrationIdCountries: PropTypes.arrayOf(PropTypes.string).isRequired,
  mid: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
  forcedValidationConfig: PropTypes.object,
};

export default compose(
  connect(state => ({
    countries: getCountries(state),
    registrationCourts: getRegistrationCourts('DE')(state),
    forcedRegistrationIdCountries: getForcedRegistrationIdCountries(state),
    goodsCategories: getGoodsCategories(state),
    defaultState: getDefaultState(state),
    t: countrySpecificTranslationParser(state),
  })),
  withFeatureToggledValidation('registrationId'),
  withCapabilityBeforeNext({
    cap: CAPS.V2_PRE_ASSESSMENT_PATCH,
  }),
)(PreAssessmentSmeCompanyDetails);
